import Hls, { Level, LevelDetails, MediaFragment, MediaPlaylist } from 'hls.js';
import {
  HlsTimelineBufferRow,
  HlsTimelinePlaylistRow,
  HlsTimelineStats,
  HlsTimelineTimeRange,
} from './hls-timeline-stats-types';

/** Strips LL-HLS delivery directive query params so playlist URLs can be matched reliably. */
export function stripDeliveryDirectives(url: string): string {
  if (url === '') {
    return url;
  }
  try {
    const webUrl = new URL(url);
    webUrl.searchParams.delete('_HLS_msn');
    webUrl.searchParams.delete('_HLS_part');
    webUrl.searchParams.delete('_HLS_skip');
    webUrl.searchParams.sort();
    return webUrl.href;
  } catch {
    return url.replace(/[?&]_HLS_(?:msn|part|skip)=[^?&]+/g, '');
  }
}

type BufferedFragment = {
  start: number;
  duration: number;
  stats?: { loaded?: number };
  byteLength?: number | null;
};

/** Returns how much of a fragment timeline overlaps the buffered ranges, from 0 to 1. */
export function getFragmentBufferOverlapRatio(
  fragment: BufferedFragment,
  ranges: HlsTimelineTimeRange[],
): number {
  const fragmentStart = fragment.start;
  const fragmentEnd = fragment.start + fragment.duration;
  if (!fragment.duration) {
    return 0;
  }

  let overlapDuration = 0;
  for (const range of ranges) {
    const overlapStart = Math.max(fragmentStart, range.start);
    const overlapEnd = Math.min(fragmentEnd, range.end);
    if (overlapEnd > overlapStart) {
      overlapDuration += overlapEnd - overlapStart;
    }
  }

  return Math.min(1, overlapDuration / fragment.duration);
}

/** Estimates buffered bytes by prorating downloaded fragment sizes over buffered ranges. */
export function estimateBufferedBytes(
  fragments: BufferedFragment[],
  ranges: HlsTimelineTimeRange[],
): number {
  if (!ranges.length) {
    return 0;
  }

  let bytes = 0;
  fragments.forEach((fragment) => {
    const overlapRatio = getFragmentBufferOverlapRatio(fragment, ranges);
    if (!overlapRatio) {
      return;
    }
    const loadedBytes = fragment.stats?.loaded ?? fragment.byteLength ?? 0;
    if (loadedBytes) {
      bytes += loadedBytes * overlapRatio;
    }
  });

  return Math.round(bytes);
}

/** Builds the map key used to track demuxed bytes appended per fragment/part. */
export function getFragmentBufferedBytesKey(
  parent: string,
  level: number,
  sn: number | 'initSegment',
  partIndex = -1,
) {
  return `${parent}|${level}|${sn}|${partIndex}`;
}

/** Rollup key for all parts of the same fragment. */
export function getFragmentBufferedBytesRollupKey(
  parent: string,
  level: number,
  sn: number | 'initSegment',
) {
  return getFragmentBufferedBytesKey(parent, level, sn);
}

/** Sums demuxed chunk bytes for fragments that overlap the buffered ranges. */
export function sumBufferedChunkBytes(
  fragments: MediaFragment[],
  ranges: HlsTimelineTimeRange[],
  chunkBytesByFragment: Map<string, number>,
  parent: string,
): number {
  if (!ranges.length) {
    return 0;
  }

  let bytes = 0;
  fragments.forEach((fragment) => {
    const overlapRatio = getFragmentBufferOverlapRatio(fragment, ranges);
    if (!overlapRatio) {
      return;
    }

    const key = getFragmentBufferedBytesRollupKey(parent, fragment.level, fragment.sn);
    const chunkBytes = chunkBytesByFragment.get(key);
    if (chunkBytes) {
      bytes += chunkBytes * overlapRatio;
    }
  });

  return Math.round(bytes);
}

/** Resolves the fragment list that feeds a given buffer track kind. */
export function getFragmentsForBufferKind(
  hls: Hls,
  bufferKind: HlsTimelineBufferRow['bufferKind'],
): MediaFragment[] {
  if (bufferKind === 'audio') {
    return hls.audioTracks[hls.audioTrack]?.details?.fragments ?? [];
  }

  const activeLevel = hls.levels[hls.currentLevel] ?? hls.levels[hls.loadLevel];
  const levelFragments = activeLevel?.details?.fragments ?? [];

  if (bufferKind === 'media') {
    const audioFragments = hls.audioTracks[hls.audioTrack]?.details?.fragments ?? [];
    return [...levelFragments, ...audioFragments];
  }

  return levelFragments;
}

/** Computes buffered bytes for a buffer row, preferring demuxed chunk sizes when tracked. */
export function getBufferRowBufferedBytes(
  hls: Hls,
  bufferKind: HlsTimelineBufferRow['bufferKind'],
  ranges: HlsTimelineTimeRange[],
  chunkBytesByFragment: Map<string, number>,
): number {
  const fragments = getFragmentsForBufferKind(hls, bufferKind);
  const parents =
    bufferKind === 'media'
      ? (['main', 'audio'] as const)
      : bufferKind === 'audio'
        ? (['audio'] as const)
        : (['main'] as const);

  const chunkBytes = parents.reduce(
    (sum, parent) => sum + sumBufferedChunkBytes(fragments, ranges, chunkBytesByFragment, parent),
    0,
  );
  if (chunkBytes) {
    return chunkBytes;
  }

  return estimateBufferedBytes(fragments, ranges);
}

/** Resolves fragments for a playlist stats row from the current hls.js manifest state. */
export function getFragmentsForPlaylistRow(
  hls: Hls,
  row: Pick<HlsTimelinePlaylistRow, 'id' | 'playlistKind' | 'levelIndex'>,
): MediaFragment[] {
  if (row.playlistKind === 'level') {
    const level =
      hls.levels.find((entry, index) => (entry.id || index) === row.levelIndex) ??
      hls.levels[Number(row.id.replace('level-', ''))];
    return level?.details?.fragments ?? [];
  }

  if (row.playlistKind === 'audioTrack') {
    const index = Number(row.id.replace('audio-', ''));
    return hls.audioTracks[index]?.details?.fragments ?? [];
  }

  const index = Number(row.id.replace('subtitle-', ''));
  return hls.subtitleTracks[index]?.details?.fragments ?? [];
}

/** Computes buffered bytes for a playlist row against the media element buffered ranges. */
export function getPlaylistBufferedBytes(
  fragments: MediaFragment[],
  mediaRanges: HlsTimelineTimeRange[],
  chunkBytesByFragment: Map<string, number>,
  playlistKind: HlsTimelinePlaylistRow['playlistKind'],
): number {
  const parent =
    playlistKind === 'audioTrack' ? 'audio' : playlistKind === 'level' ? 'main' : 'subtitle';
  const chunkBytes = sumBufferedChunkBytes(fragments, mediaRanges, chunkBytesByFragment, parent);
  if (chunkBytes) {
    return chunkBytes;
  }
  return estimateBufferedBytes(fragments, mediaRanges);
}

/** Converts a TimeRanges object into plain start/end tuples. */
export function timeRangesToTuples(timeRanges: TimeRanges): HlsTimelineTimeRange[] {
  const ranges: HlsTimelineTimeRange[] = [];
  for (let i = 0; i < timeRanges.length; i++) {
    ranges.push({ start: timeRanges.start(i), end: timeRanges.end(i) });
  }
  return ranges;
}

/** Derives buffer duration metrics relative to the current playback position. */
export function computeBufferMetrics(
  ranges: HlsTimelineTimeRange[],
  currentTime: number | null,
): Pick<
  HlsTimelineBufferRow,
  'rangeCount' | 'totalDuration' | 'forwardBuffer' | 'backBuffer' | 'bufferStart' | 'bufferEnd'
> {
  const rangeCount = ranges.length;
  const totalDuration = ranges.reduce((sum, range) => sum + (range.end - range.start), 0);
  const bufferStart = ranges[0]?.start ?? null;
  const bufferEnd = ranges[rangeCount - 1]?.end ?? null;

  let forwardBuffer: number | null = null;
  let backBuffer: number | null = null;

  if (currentTime != null) {
    for (const range of ranges) {
      if (currentTime >= range.start && currentTime <= range.end) {
        forwardBuffer = range.end - currentTime;
        backBuffer = currentTime - range.start;
        break;
      }
    }
  }

  return {
    rangeCount,
    totalDuration,
    forwardBuffer,
    backBuffer,
    bufferStart,
    bufferEnd,
  };
}

/** Builds a buffer stats row from buffered time ranges. */
export function buildBufferRow(
  id: string,
  bufferKind: HlsTimelineBufferRow['bufferKind'],
  ranges: HlsTimelineTimeRange[],
  currentTime: number | null,
  trackId?: string,
): HlsTimelineBufferRow {
  return {
    rowType: 'buffer',
    id,
    bufferKind,
    trackId,
    ranges,
    bufferedBytes: 0,
    ...computeBufferMetrics(ranges, currentTime),
  };
}

/** Aggregates fragment and part counters from a parsed HLS playlist. */
export function getPlaylistCounters(details: LevelDetails | undefined) {
  const fragments = details?.fragments ?? [];
  const parts = details?.partList ?? [];
  let loadedFragments = 0;
  let loadedBytes = 0;
  let totalBytes = 0;

  fragments.forEach((fragment) => {
    const stats = fragment.stats;
    if (stats?.loaded) {
      loadedFragments += 1;
      loadedBytes += stats.loaded;
    }
    if (stats?.total) {
      totalBytes += stats.total;
    } else if (fragment.byteLength) {
      totalBytes += fragment.byteLength;
    }
  });

  return {
    fragmentCount: fragments.length,
    partCount: parts.length,
    loadedFragments,
    loadedBytes,
    totalBytes,
    bufferedBytes: 0,
    playlistDuration: details?.totalduration ?? null,
    targetDuration: details?.targetduration ?? null,
    live: details?.live ?? null,
  };
}

function getLevelLabel(level: Level, index: number) {
  let label = '(main playlist)';
  if (level.attrs?.BANDWIDTH) {
    label = `${getMainLevelAttribute(level)}@${level.attrs.BANDWIDTH}`;
    if (level.name) {
      label = `${label} (${level.name})`;
    }
  } else if (level.name) {
    label = level.name;
  }
  return `${label} L-${index}`;
}

function getMainLevelAttribute(level: Level) {
  return level.attrs.RESOLUTION || level.attrs.CODECS || level.attrs.AUDIO;
}

function getAudioTrackLabel(track: MediaPlaylist, index: number) {
  const label = track.lang ? `${track.name}/${track.lang}` : track.name;
  return `${label} (${track.groupId || track.attrs['GROUP-ID']}) A-${index}`;
}

function getSubtitleTrackLabel(track: MediaPlaylist, index: number) {
  const label = track.lang ? `${track.name}/${track.lang}` : track.name;
  return `${label} (${track.groupId || track.attrs['GROUP-ID']}) S-${index}`;
}

/** Builds playlist rows for all main quality levels. */
export function buildLevelPlaylistRows(
  levels: Level[],
  activeLevel: number,
  loadLevel: number,
): HlsTimelinePlaylistRow[] {
  return levels.map((level, index) => {
    const levelIndex = level.id || index;
    const counters = getPlaylistCounters(level.details);
    return {
      rowType: 'playlist',
      id: `level-${levelIndex}`,
      playlistKind: 'level',
      label: getLevelLabel(level, index),
      url: Array.isArray(level.url) ? level.url[level.urlId || 0] : level.url,
      levelIndex,
      isActive: activeLevel === index || loadLevel === index,
      ...counters,
    };
  });
}

/** Builds playlist rows for alternate audio tracks. */
export function buildAudioPlaylistRows(
  audioTracks: MediaPlaylist[],
  activeAudioTrack: number,
): HlsTimelinePlaylistRow[] {
  return audioTracks.map((track, index) => ({
    rowType: 'playlist',
    id: `audio-${index}`,
    playlistKind: 'audioTrack',
    label: getAudioTrackLabel(track, index),
    url: track.url,
    isActive: activeAudioTrack === index,
    ...getPlaylistCounters(track.details),
  }));
}

/** Builds playlist rows for subtitle tracks. */
export function buildSubtitlePlaylistRows(
  subtitleTracks: MediaPlaylist[],
  activeSubtitleTrack: number,
): HlsTimelinePlaylistRow[] {
  return subtitleTracks.map((track, index) => ({
    rowType: 'playlist',
    id: `subtitle-${index}`,
    playlistKind: 'subtitleTrack',
    label: getSubtitleTrackLabel(track, index),
    url: track.url,
    isActive: activeSubtitleTrack === index,
    ...getPlaylistCounters(track.details),
  }));
}

function padTimePart(value: string | number, length: number) {
  let text = `${value}`;
  while (text.length < length) {
    text = `0${text}`;
  }
  return text;
}

/** Formats seconds as M:SS.x for compact table display (e.g. 83.8 → 1:23.8). */
export function formatSeconds(value: number | null | undefined, digits = 1) {
  if (value == null || Number.isNaN(value)) {
    return '—';
  }

  const minutes = Math.floor(value / 60);
  const seconds = value % 60;

  if (digits > 0) {
    const [secondsInt, secondsFrac] = seconds.toFixed(digits).split('.');
    const paddedSeconds = secondsFrac
      ? `${padTimePart(secondsInt, 2)}.${secondsFrac}`
      : padTimePart(secondsInt, 2);
    return `${minutes}:${paddedSeconds}`;
  }

  return `${minutes}:${padTimePart(Math.floor(seconds), 2)}`;
}

/** Formats byte counts for compact table display. */
export function formatBytes(bytes: number) {
  if (!bytes) {
    return '0 B';
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Creates an immutable stats snapshot from collector state. */
export function createStatsSnapshot(
  bufferRows: HlsTimelineBufferRow[],
  playlistRows: HlsTimelinePlaylistRow[],
  currentTime: number | null,
  maxBufferLength: number | null,
  forwardBufferInfo: HlsTimelineStats['forwardBufferInfo'],
): HlsTimelineStats {
  return {
    bufferRows,
    playlistRows,
    currentTime,
    maxBufferLength,
    forwardBufferInfo,
    updatedAt: Date.now(),
  };
}
